import { Citation } from "@/constants/citation.constant";

export type PolicyLinkMap = {
  byFullPath: Map<string, string>;
  byDocumentName: Map<string, string>;
};

const POLICY_LINK_FILE_PATH = "/policy-procedure-link.xlsx";
let policyLinkMapPromise: Promise<PolicyLinkMap> | null = null;

export function getFileName(fullPath: string) {
  if (!fullPath) return '';
  const decoded = (() => {
    try { return decodeURIComponent(fullPath); } catch { return fullPath; }
  })();
  const idx = decoded.lastIndexOf('/');
  return idx !== -1 ? decoded.substring(idx + 1) : decoded;
}

export function normalizePolicyPath(value: string) {
  const decoded = (() => {
    try { return decodeURIComponent(value); } catch { return value; }
  })();

  return decoded
    .replace(/\\/g, "/")
    .replace(/\.[a-z0-9]+(?:$|\?)/i, "")
    .replace(/\?.*$/, "")
    .replace(/\/+/g, "/")
    .replace(/^\/+|\/+$/g, "")
    .trim()
    .toLowerCase();
}

function normalizeHeader(value: unknown) {
  return String(value || "").trim().toLowerCase();
}

function cellValue(value: unknown) {
  return String(value || "").trim();
}

export async function loadPolicyLinkMap(): Promise<PolicyLinkMap> {
  if (policyLinkMapPromise) return policyLinkMapPromise;

  policyLinkMapPromise = (async () => {
    const response = await fetch(POLICY_LINK_FILE_PATH);
    if (!response.ok) {
      throw new Error(`Failed to load ${POLICY_LINK_FILE_PATH}`);
    }

    const XLSX = await import("xlsx");
    const workbook = XLSX.read(await response.arrayBuffer(), { type: "array" });
    const worksheet = workbook.Sheets.Data || workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
      header: 1,
      defval: "",
      raw: false,
    });

    const headerRowIndex = rows.findIndex((row) =>
      row.some((cell) => normalizeHeader(cell) === "document name") &&
      row.some((cell) => normalizeHeader(cell) === "folder path") &&
      row.some((cell) => normalizeHeader(cell) === "primary document link")
    );

    if (headerRowIndex === -1) {
      throw new Error("Policy link workbook is missing required columns.");
    }

    const headers = rows[headerRowIndex].map(normalizeHeader);
    const documentNameIndex = headers.indexOf("document name");
    const folderPathIndex = headers.indexOf("folder path");
    const primaryLinkIndex = headers.indexOf("primary document link");
    const byFullPath = new Map<string, string>();
    const nameEntries = new Map<string, Set<string>>();

    rows.slice(headerRowIndex + 1).forEach((row) => {
      const documentName = cellValue(row[documentNameIndex]);
      const folderPath = cellValue(row[folderPathIndex]);
      const primaryLink = cellValue(row[primaryLinkIndex]);

      if (!documentName || !primaryLink) return;

      const documentNameKey = normalizePolicyPath(documentName);
      const fullPathKey = normalizePolicyPath(`${folderPath}/${documentName}`);

      byFullPath.set(fullPathKey, primaryLink);

      const existing = nameEntries.get(documentNameKey) || new Set<string>();
      existing.add(primaryLink);
      nameEntries.set(documentNameKey, existing);
    });

    const byDocumentName = new Map<string, string>();
    nameEntries.forEach((links, documentName) => {
      if (links.size === 1) {
        byDocumentName.set(documentName, Array.from(links)[0]);
      }
    });

    return { byFullPath, byDocumentName };
  })();

  return policyLinkMapPromise;
}

export function resolvePolicyLink(map: PolicyLinkMap, citation: Citation | null) {
  const pathSource = citation?.file_path || citation?.source_file || citation?.name || "";
  if (!pathSource) return "";

  const normalizedPath = normalizePolicyPath(pathSource);
  const normalizedFileName = normalizePolicyPath(getFileName(pathSource));

  return (
    map.byFullPath.get(normalizedPath) ||
    map.byDocumentName.get(normalizedFileName) ||
    ""
  );
}
